import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@boma/app-config';
import { IPaginatedResponse } from '@boma/client-shared';
import {
  IInventoryCheckItem,
  IInventoryCheckStockCheckSaveResponse,
  IInventoryCheckStockTake,
  IInventoryCheckStockTakeBatch,
  IInventoryCheckStockTakeCreateBatchResponse,
  IInventoryCheckStockTakeCreateResponse,
  IInventoryCheckStockTakeDetails,
  IInventoryCheckStockTakeProducts,
  IInventoryCheckStockTakeSearch,
  InventoryCheckEventType,
} from '@boma/supplier-portal/shared/entities';
import { HelperFunctionsService } from '@boma/supplier-portal/shared/services';
import { map } from 'rxjs/operators';
import { InventoryCheckDailySummaryResponse } from '../../../../entities/src/lib/definitions/inventory-check-daily-summary';

@Injectable({
  providedIn: 'root',
})
export class InventoryCheckService {
  private base: string;
  private version: string;

  constructor(
    private helperService: HelperFunctionsService,
    @Inject(APP_CONFIG) private configuration: any,
    private httpClient: HttpClient
  ) {
    this.base = configuration.apiRoute;
    this.version = configuration.apiVersion;
  }

  /**
   * Create an Inventory Check
   * @param entityId
   * @param details
   * @returns IInventoryCheckStockTakeCreateResponse
   */
  createInventoryCheck(entityId: string, details: IInventoryCheckStockTake) {
    const params = this.helperService.sanitizeParams(details);
    return this.httpClient.post<IInventoryCheckStockTakeCreateResponse>(
      `${this.base}api/v${this.version}/${entityId}/InventoryCheck/`,
      {
        ...params,
      }
    );
  }

  /**
   *  Create Multiple Inventory Checks
   * @param entityId
   * @param details
   * @returns IInventoryCheckStockTakeCreateBatchResponse
   */
  createInventoryCheckBatch(
    entityId: string,
    details: IInventoryCheckStockTakeBatch
  ) {
    const params = this.helperService.sanitizeParams(details);
    return this.httpClient.post<IInventoryCheckStockTakeCreateBatchResponse>(
      `${this.base}api/v${this.version}/${entityId}/InventoryCheck/`,
      {
        ...params,
      }
    );
  }

  /**
   * Update Inventory Check
   * @param id
   * @param entityId
   * @param details
   * @returns
   */
  updateInventoryCheck(
    id: string,
    entityId: string,
    details: IInventoryCheckStockTake
  ) {
    const params = this.helperService.sanitizeParams(details);
    return this.httpClient.put<IInventoryCheckStockTakeDetails>(
      `${this.base}api/v${this.version}/${entityId}/InventoryCheck/${id}`,
      {
        ...params,
      }
    );
  }

  /**
   * List inventory checks
   */
  loadInventoryChecks(
    entityId: string,
    details: {
      locationIds?: string[];
      page?: number;
      perPage?: number;
      startDate?: string;
      endDate?: string;
      types?: number[];
      eventTypes?: string[];
    }
  ) {
    details.eventTypes = details?.eventTypes.length
      ? details.eventTypes.map((x) => {
          if (x) {
            return this.helperService.removeWhiteSpaces(x); // we need to pass the names without spaces... todo could be better done by swapping the enum values...
          } else {
            return null;
          }
        })
      : [];
    const params = this.helperService.sanitizeParams(details);

    console.log(params);
    //TODO API SEEMS TO BE IGNORING THE PAGE SIZE

    return this.httpClient.post<
      IPaginatedResponse<IInventoryCheckStockTakeDetails>
    >(
      `${this.base}api/v${this.version}/${entityId}/InventoryCheck/List`,
      params
    );
  }

  /**
   * List inventory checks
   */
  loadInventoryCheckDetails(id: string, entityId: string, locationId: string) {
    return this.httpClient.post<IInventoryCheckStockTakeDetails>(
      `${this.base}api/v${this.version}/${entityId}/InventoryCheck/${id}/${locationId}`,
      {}
    );
  }

  updateInventoryCheckEvent(
    id: string,
    entityId: string,
    locationId: string,
    eventType: InventoryCheckEventType
  ) {
    return this.httpClient.post<IInventoryCheckStockTakeDetails>(
      `${this.base}api/v${this.version}/${entityId}/InventoryCheck/${id}/Events/${locationId}`,
      {
        eventType: this.helperService.getEnumKeyWithValue(
          eventType,
          InventoryCheckEventType
        ),
      }
    );
  }

  /**
   *  Add Items to inventory check
   * @param id
   * @param entityId
   * @param locationId
   * @param details
   * @returns
   */
  updateProductsAndQtyForStockTakeLocation(
    id: string,
    entityId: string,
    locationId: string,
    details: IInventoryCheckStockTakeProducts
  ) {
    const params = this.helperService.sanitizeParams(details);
    return this.httpClient.post<IInventoryCheckStockCheckSaveResponse>(
      `${this.base}api/v${this.version}/${entityId}/InventoryCheck/${id}/Items/${locationId}`,

      params
    );
  }

  listProductsAndQtyForStockTakeLocation(
    id: string,
    entityId: string,
    locationId: string,
    details: IInventoryCheckStockTakeSearch
  ) {
    const params = this.helperService.sanitizeParams(details);
    return this.httpClient.post<IPaginatedResponse<IInventoryCheckItem>>(
      `${this.base}api/v${this.version}/${entityId}/InventoryCheck/${id}/Items/${locationId}/List`,
      params
    );
  }

  GetDailySummary(entityId: string) {
    return this.httpClient
      .post<InventoryCheckDailySummaryResponse>(
        `${this.base}api/v${this.version}/${entityId}/InventoryCheck/KPI/DailySummary`,
        {}
      )
      .pipe(map((r) => new InventoryCheckDailySummaryResponse(r)));
  }
}
